import React, { useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { Pin } from '../types';
import { nextPinLabel } from '../utils/pins';
import { COLORS } from '../utils/constants';

interface ConnectorTypeFormProps {
  stateManager: StateManager;
  /** Existing type to edit, or undefined to create a new one (added only on save). */
  typeId?: string;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '13px',
  color: '#636e72'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  color: 'white'
};

/**
 * Form dialog for a connector type: its name, a note, and the pin list. Each pin
 * has a number and an optional name, and pins can be added or removed. Pins keep
 * their ids across edits so connectors already using the type stay consistent.
 * A new type is created in the catalog only when the form is saved.
 */
export const ConnectorTypeForm: React.FC<ConnectorTypeFormProps> = ({ stateManager, typeId, onClose }) => {
  const type = typeId ? stateManager.getState().connectorTypes.find(t => t.id === typeId) : undefined;
  const isNew = !typeId;

  const [name, setName] = useState(type?.name ?? '');
  const [note, setNote] = useState(type?.note ?? '');
  const [pins, setPins] = useState<Pin[]>(type?.pins ?? []);

  // An edit target that no longer exists (e.g. deleted elsewhere) has nothing to show.
  if (typeId && !type) {
    return null;
  }

  const updatePin = (id: string, updates: Partial<Pin>) => {
    setPins(current => current.map(pin => (pin.id === id ? { ...pin, ...updates } : pin)));
  };

  const removePin = (id: string) => {
    setPins(current => current.filter(pin => pin.id !== id));
  };

  const addPin = () => {
    setPins(current => [...current, { id: stateManager.generateUniqueId(), label: nextPinLabel(current), name: '' }]);
  };

  const save = () => {
    const cleaned = pins
      .filter(pin => pin.label.trim() !== '')
      .map(pin => ({ id: pin.id, label: pin.label.trim(), name: pin.name?.trim() ? pin.name.trim() : undefined }));
    const resolvedName = name.trim() || type?.name || 'New Type';
    const resolvedNote = note.trim() || undefined;
    if (typeId) {
      stateManager.updateConnectorType(typeId, { name: resolvedName, note: resolvedNote, pins: cleaned });
    } else {
      stateManager.addConnectorType({ id: stateManager.generateUniqueId(), name: resolvedName, note: resolvedNote, pins: cleaned });
    }
    onClose();
  };

  const remove = () => {
    if (typeId) {
      stateManager.removeConnectorType(typeId);
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20000
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { onClose(); } }}
    >
      <div
        style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', width: '460px', maxHeight: '80vh', overflowY: 'auto' }}
        onKeyDown={(e) => { if (e.key === 'Escape') { onClose(); } }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#2d3436' }}>{isNew ? 'New Connector Type' : 'Connector Type'}</h3>

        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus style={inputStyle} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Note</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. manufacturer, part number" style={inputStyle} />
        </div>

        <label style={labelStyle}>Pins</label>
        <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#b2bec3', margin: '2px 0 6px 0' }}>
          <span style={{ flex: '0 0 90px' }}>Number</span>
          <span style={{ flex: 1 }}>Name (optional)</span>
          <span style={{ flex: '0 0 32px' }} />
        </div>
        {pins.map(pin => (
          <div key={pin.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
            <input
              style={{ ...inputStyle, flex: '0 0 90px' }}
              value={pin.label}
              onChange={(e) => updatePin(pin.id, { label: e.target.value })}
              placeholder="1"
            />
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={pin.name ?? ''}
              onChange={(e) => updatePin(pin.id, { name: e.target.value })}
              placeholder="Ignition"
            />
            <button
              type="button"
              onClick={() => removePin(pin.id)}
              title="Remove pin"
              style={{ ...buttonStyle, flex: '0 0 32px', padding: '8px 0', backgroundColor: COLORS.DANGER }}
            >
              ×
            </button>
          </div>
        ))}

        <button type="button" onClick={addPin} style={{ ...buttonStyle, backgroundColor: COLORS.SECONDARY, marginTop: '4px' }}>
          Add Pin
        </button>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '20px' }}>
          {isNew ? (
            <span />
          ) : (
            <button type="button" onClick={remove} style={{ ...buttonStyle, backgroundColor: COLORS.DANGER }}>
              Delete Type
            </button>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white', color: '#636e72', cursor: 'pointer', fontSize: '14px' }}
            >
              Cancel
            </button>
            <button type="button" onClick={save} style={{ ...buttonStyle, backgroundColor: COLORS.ACTION }}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
